"use client";

import Link from "next/link";
import Image from "next/image";
import { ChevronRight } from "lucide-react";
import { Container } from "@/components/container";
import { AUTHORS_DATA } from "@/lib/authors-data";

export function AuthorsHubView() {
  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-primary-foreground pb-32">
      <Container className="pt-10 md:pt-14">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-8 flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight aria-hidden="true" className="h-3 w-3 text-muted-foreground/60" />
          <span aria-current="page" className="font-semibold text-foreground">Author</span>
        </nav>

        {/* Page Title */}
        <div className="mb-12">
          <h1 className="text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
            Authors
          </h1>
          <p className="mt-3 text-base text-muted-foreground max-w-xl">
            Meet the engineers, product architects, and leaders building the future of enterprise voice AI.
          </p>
        </div>

        {/* Authors Grid */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
          {AUTHORS_DATA.map((author, index) => (
            <Link
              key={author.slug}
              href={`/author/${author.slug}`}
              className="group relative flex flex-col justify-end overflow-hidden rounded-3xl bg-muted shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl aspect-[4/5]"
            >
              {/* Profile Image */}
              <Image
                src={author.image}
                alt={author.name}
                fill
                unoptimized={author.image.endsWith(".svg")}
                className="object-cover object-top transition-transform duration-500 group-hover:scale-105"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                priority={index < 2}
              />

              {/* Gradient Scrim for Contrast */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent transition-opacity group-hover:from-black/90" />

              {/* Text Info */}
              <div className="relative z-10 p-4 sm:p-6">
                <h3 className="text-base font-bold tracking-tight text-white transition-colors group-hover:text-white/80 sm:text-xl">
                  {author.name}
                </h3>
                <p className="mt-1 text-xs sm:text-sm font-medium text-white/70">
                  {author.role}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </Container>
    </div>
  );
}
